<template>
  <Teleport to="body">
    <Transition name="overlay">
      <div v-if="show" class="fixed inset-0 z-[700] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl h-[600px] max-h-[90vh] flex flex-col">
          <div class="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
            <div class="min-w-0">
              <h3 class="text-lg font-semibold text-gray-900">Channel Status History</h3>
              <p class="text-sm text-gray-400 mt-0.5 truncate">{{ subtitle || "Every recorded transition of this channel's status" }}</p>
            </div>
            <button @click="$emit('close')" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0 ml-4">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="flex items-center gap-2 px-6 pb-3 shrink-0">
            <button
              v-for="p in presets"
              :key="p.id"
              @click="setPreset(p.id)"
              :disabled="loading"
              :class="[
                'h-8 px-3 rounded-lg text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
                preset === p.id
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              ]"
            >{{ p.label }}</button>
          </div>
          <div class="flex-1 overflow-y-auto px-6 pb-4 relative">
            <table v-if="history.length" class="w-full text-sm">
              <thead class="sticky top-0 bg-white z-10">
                <tr class="text-left text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200">
                  <th class="py-2 pr-3">Status</th>
                  <th class="py-2 pr-3">When</th>
                  <th class="py-2 pr-3">Duration</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="row in history" :key="row._id">
                  <td class="py-2 pr-3">
                    <span :class="statusBadgeClass(row.status)">{{ row.status }}</span>
                  </td>
                  <td class="py-2 pr-3 text-sm text-gray-700">{{ $filters.formatDatetime(row.created_at) }}</td>
                  <td class="py-2 pr-3 text-sm font-data text-gray-500">{{ row.duration_text || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else-if="!loading" class="text-sm text-gray-400 py-10 text-center">No status history yet.</p>

            <!-- Loading overlay — sits above whatever's currently rendered so
                 the previous rows stay visible while a new preset loads. -->
            <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
              <svg class="w-6 h-6 animate-spin text-[var(--color-primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
                <path d="M22 12a10 10 0 01-10 10" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import api from '../services/api.js'
import { notifyError } from '../services/notifications.js'

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '24h',   label: 'Last 24 Hours' },
  { id: '7d',    label: 'Last 7 Days' }
]

export default {
  name: 'StatusHistoryModal',
  props: {
    show: { type: Boolean, default: false },
    livestreamId: { type: String, default: '' },
    subtitle: { type: String, default: '' }
  },
  emits: ['close'],

  data() {
    return {
      presets: PRESETS,
      history: [],
      loading: false,
      preset: 'today',
    }
  },

  watch: {
    show(val) {
      if (!val) return
      this.preset = 'today'
      this.history = []
      this.fetchHistory()
    }
  },

  methods: {
    async setPreset(preset) {
      if (this.loading || this.preset === preset) return
      this.preset = preset
      await this.fetchHistory()
    },

    presetRange(preset) {
      const now = new Date()
      const dayMs = 24 * 60 * 60 * 1000
      if (preset === '24h') return { dateFrom: new Date(now.getTime() - dayMs),     dateTo: now }
      if (preset === '7d')  return { dateFrom: new Date(now.getTime() - 7 * dayMs), dateTo: now }
      const start = new Date(now); start.setHours(0, 0, 0, 0)
      return { dateFrom: start, dateTo: now }
    },

    async fetchHistory() {
      if (!this.livestreamId) return
      this.loading = true
      try {
        const { dateFrom, dateTo } = this.presetRange(this.preset)
        const res = await api.get(`/livestreams/${this.livestreamId}/status-history`, {
          params: { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() }
        })
        const items = res?.data?.items || res?.data || []
        const sorted = (Array.isArray(items) ? items : [])
          .slice()
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        // Each row's duration is "how long this status was active before
        // the next change", which (in newest-first order) is the row
        // above's timestamp minus this row's timestamp. The first row is
        // the current status and has no closed duration yet.
        this.history = sorted.map((row, i) => {
          if (i === 0) return { ...row, duration_text: '' }
          const nextChangeMs = new Date(sorted[i - 1].created_at).getTime()
          const thisMs = new Date(row.created_at).getTime()
          return { ...row, duration_text: this.formatDuration(nextChangeMs - thisMs) }
        })
      } catch (err) {
        notifyError(err?.message || 'Failed to load status history')
        this.history = []
      } finally {
        this.loading = false
      }
    },

    formatDuration(ms) {
      if (ms == null || !isFinite(ms) || ms < 0) return ''
      const s = Math.floor(ms / 1000)
      if (s < 60) return `${s}s`
      if (s < 3600) {
        const m = Math.floor(s / 60)
        const rem = s % 60
        return rem ? `${m}m ${rem}s` : `${m}m`
      }
      if (s < 86400) {
        const h = Math.floor(s / 3600)
        const m = Math.floor((s % 3600) / 60)
        return m ? `${h}h ${m}m` : `${h}h`
      }
      const d = Math.floor(s / 86400)
      const h = Math.floor((s % 86400) / 3600)
      return h ? `${d}d ${h}h` : `${d}d`
    },

    statusBadgeClass(status) {
      // Same vocabulary as transcodingStatusClass in lives/edit.vue and
      // lives/list.vue — keeps the status badges colored identically.
      if (!status) return 'badge-idle'
      const s = status.toLowerCase()
      if (['streaming', 'success', 'running'].includes(s)) return 'badge-live'
      if (['failed', 'error'].includes(s)) return 'badge-error'
      if (['provisioning', 'awaiting', 'awaiting_input', 'starting'].includes(s)) return 'badge-warning'
      return 'badge-idle'
    }
  }
}
</script>
